import { useQuery } from '@apollo/client/react';
import { useTranslation } from 'next-i18next/pages';

import { CandidateDocument } from '@pflegia/data-access/candidate.generated';
import { ShiftSystem } from '@pflegia/types/api/candidate/shift-system';
import { JobPosition } from '@pflegia/types/api/job-position/job-position';
import { doesShiftSystemMatchShiftType } from '@pflegia/types/api/job-position/shift-type-match';
import { useAuth } from '@pflegia/ui/contexts/auth.context';
import useCandidateLocationForJob from '@pflegia/ui/shared/hooks/use-candidate-location-for-job';

import MatchReasonsBlock from './MatchReasonsBlock';
import MatchReasonsSkeleton from './MatchReasonsSkeleton';
import { getDistanceInKm } from './format-distance';
import { getMatchReasons } from './get-match-reasons';

export interface MatchReasonsProps {
  jobPosition: JobPosition;
  matchLoading?: boolean;
  candidateLocation: ReturnType<typeof useCandidateLocationForJob>['candidateLocation'];
  showSalary: boolean;
}

const MatchReasons: React.FC<MatchReasonsProps> = ({ jobPosition, matchLoading, candidateLocation, showSalary }) => {
  const { t } = useTranslation('jobDetails');
  const authenticated = useAuth();

  const { data: candidateData, loading: candidateLoading } = useQuery(CandidateDocument, { skip: !authenticated });
  const candidate = candidateData?.candidate;

  const reasons = getMatchReasons(t, {
    distanceKm: getDistanceInKm(candidateLocation?.location?.coordinates, jobPosition.geoLocation?.location?.coordinates),
    maxDistanceKm: candidate?.maxDistance,
    salaryExpectation: candidate?.salaryExpectation,
    salaryLow: jobPosition.salaryLow,
    salaryHigh: jobPosition.salaryHigh,
    canShowSalary:
      Boolean(jobPosition.employer.showSalary) && Boolean(jobPosition.showSalary) && Boolean(jobPosition.showSalaryDetails) && showSalary,
    shiftMatch: doesShiftSystemMatchShiftType(candidate?.shiftSystem as ShiftSystem[] | null | undefined, jobPosition.shiftType),
    formalLanguage: jobPosition.employer.formalLanguage,
  });

  if (authenticated && (candidateLoading || matchLoading)) return <MatchReasonsSkeleton />;

  if (reasons.length === 0) return null;

  return <MatchReasonsBlock reasons={reasons} formalLanguage={jobPosition.employer.formalLanguage} />;
};

export default MatchReasons;
